import React from "react";
import { Section, Eyebrow } from "../components/ui";
import {
  profile,
  howIWork,
  skills,
  experience,
  ventures,
  education,
  awards,
} from "../data/experience";

const Row = ({ item }) => (
  <div className="border-b border-line py-8 first:pt-0 last:border-0">
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
      <h3 className="text-xl font-bold text-ink">
        {item.org} <span className="font-normal text-ink-soft">/ {item.role}</span>
      </h3>
      {item.period && (
        <span className="shrink-0 text-sm font-medium text-ink-faint">{item.period}</span>
      )}
    </div>
    {item.sub && <p className="mt-1 text-sm text-ink-faint">{item.sub}</p>}
    {item.context && <p className="mt-3 text-ink-soft">{item.context}</p>}
    {item.bullets && (
      <ul className="mt-4 space-y-2">
        {item.bullets.map((b, i) => (
          <li key={i} className="flex gap-3 text-ink-soft">
            <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    )}
    {item.stack && (
      <p className="mt-4 text-sm text-ink-faint">
        <span className="font-medium text-ink-soft">Stack: </span>
        {item.stack}
      </p>
    )}
  </div>
);

const CV = () => (
  <main>
    <Section>
      <div>
        <Eyebrow>Curriculum Vitae</Eyebrow>
        <h1 className="text-5xl font-black tracking-tight text-ink sm:text-6xl">
          {profile.name}
        </h1>
        <p className="mt-2 text-lg font-light text-ink-soft">{profile.role}</p>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-faint">
          <a href={`tel:${profile.phone.replace(/\s/g, "")}`} className="hover:text-ink">{profile.phone}</a>
          <a href={`mailto:${profile.email}`} className="hover:text-ink">{profile.email}</a>
          <span>{profile.site}</span>
          <a href="/Simon-Stead-CV.pdf" className="font-medium text-accent hover:text-accent-dark">
            Download as PDF →
          </a>
        </div>

        <div className="mt-12 max-w-prose space-y-5 text-lg leading-relaxed text-ink-soft">
          <p>{profile.summary}</p>
          <p>{profile.summary2}</p>
          <p>{profile.summary3}</p>
        </div>
      </div>
    </Section>

    <Section tone="panel">
      <Eyebrow>How I Work</Eyebrow>
      <div className="max-w-prose space-y-5 text-lg leading-relaxed text-ink-soft">
        {howIWork.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </Section>

    <Section>
      <Eyebrow>Skills</Eyebrow>
      <div className="grid gap-10 sm:grid-cols-2">
        {skills.map((s) => (
          <div key={s.t}>
            <h3 className="text-lg font-bold text-ink">{s.t}</h3>
            <p className="mt-2 text-ink-soft">{s.d}</p>
          </div>
        ))}
      </div>
    </Section>

    <Section tone="panel">
      <Eyebrow>Experience</Eyebrow>
      <div>
        {experience.map((item) => (
          <Row key={item.org + item.period} item={item} />
        ))}
      </div>
    </Section>

    <Section>
      <Eyebrow>Founder Ventures & Products</Eyebrow>
      <p className="max-w-prose text-lg text-ink-soft">
        Solo founder and technology lead across live ventures spanning generative-AI systems,
        applied AI in a regulated clinical domain, and full-stack e-commerce and consumer
        brands. I own every layer: AI/ML, architecture, product, design, and go-to-market.
      </p>
      <div className="mt-8">
        {ventures.map((item) => (
          <Row key={item.org} item={item} />
        ))}
      </div>
    </Section>

    <Section tone="panel">
      <div className="grid gap-12 sm:grid-cols-2">
        <div>
          <Eyebrow>Education</Eyebrow>
          <div className="space-y-6">
            {education.map((e) => (
              <div key={e.org}>
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="font-bold text-ink">{e.org}</h3>
                  <span className="shrink-0 text-sm text-ink-faint">{e.period}</span>
                </div>
                <p className="text-ink-soft">{e.role}</p>
                {e.detail && <p className="mt-1 text-sm text-ink-faint">{e.detail}</p>}
              </div>
            ))}
          </div>
        </div>
        <div>
          <Eyebrow>Awards</Eyebrow>
          <ul className="space-y-2 text-ink-soft">
            {awards.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  </main>
);

export default CV;
